import React, { FC, memo } from 'react';

import { TEXTS } from 'application/pages/BillingPage/renderer/texts';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { useNavigate } from 'application/providers/RouterProvider';
import { useOnboardingActionContext, useOnboardingContext } from 'application/components/common';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';

import { LicensesList, LicensesUnpaidList, ChangeAutoRenewalButton } from './components';
import useLicensesModalViewModel from './useLicensesModalViewModel';

import styles from './index.module.scss';

const LicensesModal: FC<ModalProps[ModalTypes.LICENSES]> = ({
  openModal,
  close,
  payFrequencyMonth = 1,
  unpaidLicensesOnly = false,
  isViewMode = false,
}) => {
  const notification = useNotificationContext();
  const navigate = useNavigate();
  const { flowType, currentPoint } = useOnboardingContext();
  const { setCurrentPoint, clearCurrentPoint } = useOnboardingActionContext();

  const viewModel = useLicensesModalViewModel({
    notification,
    unpaidLicensesOnly,
    isViewMode,
    flowType,
    currentPoint,
    openModal,
    close,
    navigate,
    setCurrentPoint,
    clearCurrentPoint,
    closeModalButton: (
      <Button
        key="button_confirm_autorenewal"
        elementId="button_confirm_autorenewal"
        onClick={() => close(2)}>
        {TEXTS.BILLING_AUTORENEWAL_SUSPENDED_SUBMIT}
      </Button>
    ),
  });

  const getActionButtons = () => {
    const buttons: React.ReactNode[] = [];

    if (!viewModel) return buttons;

    const {
      viewMode,
      isChangeAutoRenewalButtonDisabled,
      licensesSubscriptionCost,
      isSubmitLoading,
      isNeedPayment,
      bindCardVisible,
      isLinkingInProcess,
      isHintShow,
      isPendingAcceptanceAdvanced,
      setEditMode,
      handleBindCard,
      handleChangeAutoRenewal,
      openAcceptanceAdvancedSigning,
    } = viewModel;

    if (viewMode) {
      buttons.push(
        <Button
          key="button_view_mode_ok"
          elementId="button_view_mode_ok"
          type={ButtonType.PRIMARY}
          onClick={() => close()}>
          {TEXTS.BILLING_LICENSES_OK}
        </Button>
      );

      buttons.push(
        <Button
          key="button_view_mode_set_edit"
          elementId="button_view_mode_set_edit"
          type={ButtonType.SECONDARY}
          onClick={setEditMode}>
          {TEXTS.BILLING_LICENSES_SET_EDIT_MODE}
        </Button>
      );
    } else {
      if (bindCardVisible) {
        buttons.push(
          <Button
            elementId="button_bind_card"
            type={ButtonType.PRIMARY}
            isLoading={isSubmitLoading}
            onClick={handleBindCard}>
            {TEXTS.BILLING_BIND_CARD_BUTTON_TEXT}
          </Button>
        );
      }

      if (isPendingAcceptanceAdvanced) {
        buttons.push(
          <Button
            elementId="button_acceptance_advanced_open"
            type={ButtonType.PRIMARY}
            onClick={() => {
              openAcceptanceAdvancedSigning();
              close();
            }}>
            {TEXTS.BILLING_LICENSES_ACCEPTANCE_ADVANCED_TEXT}
          </Button>
        );
      }

      buttons.push(
        <ChangeAutoRenewalButton
          key="button_submit_licenses"
          isDisabled={isChangeAutoRenewalButtonDisabled || isPendingAcceptanceAdvanced}
          isLoading={isSubmitLoading}
          isNeedPayment={isNeedPayment}
          licensesSubscriptionCost={licensesSubscriptionCost}
          isLinkingInProcess={isLinkingInProcess}
          isHintShow={isHintShow}
          isPendingAcceptanceAdvanced={isPendingAcceptanceAdvanced}
          onClick={handleChangeAutoRenewal}
        />
      );
    }

    return buttons;
  };

  return (
    <ModalScreen
      title={
        viewModel?.hasInactiveLicenses
          ? TEXTS.BILLING_LICENSES_RESUME_MODAL_TITLE(payFrequencyMonth === 1)
          : TEXTS.BILLING_LICENSES_MODAL_TITLE
      }
      titlePlacement="left"
      onClose={viewModel?.handleLicensesModalClose}
      containerClassName={styles.licensesModalContainer}
      contentClassName={styles.licensesModalContent}
      containerActionButtonsClassName={styles.licensesModalActionButtons}
      actionButtons={getActionButtons()}>
      {viewModel ? (
        <>
          {unpaidLicensesOnly ? (
            <LicensesUnpaidList
              unpaidLicenses={viewModel.unpaidLicenses}
              toEnableAutorenewal={viewModel.toEnableAutorenewal}
              dispatch={viewModel.dispatch}
              setSubscriptionCost={viewModel.setSubscriptionCost}
            />
          ) : (
            <LicensesList
              totalLicenses={viewModel.totalLicenses}
              toSuspendAutorenewal={viewModel.toSuspendAutorenewal}
              toEnableAutorenewal={viewModel.toEnableAutorenewal}
              dispatch={viewModel.dispatch}
              setSubscriptionCost={viewModel.setSubscriptionCost}
              isAllLicensesDisabled={viewModel.viewMode}
            />
          )}

          {viewModel.isPendingAcceptanceAdvanced ? (
            <Info
              type={InfoTypes.WARNING}
              className={styles.licensesModalHintInfo}>
              {TEXTS.BILLING_LICENSES_ACCEPTANCE_ADVANCED_PENDING}
            </Info>
          ) : (
            <Info className={styles.licensesModalHintInfo}>
              {TEXTS.BILLING_LICENSES_UNPAID_MODAL_INFO(payFrequencyMonth === 1)}
            </Info>
          )}
        </>
      ) : (
        <div className={styles.licensesSkeleton} />
      )}
    </ModalScreen>
  );
};

export default memo(LicensesModal);
